import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { getLeadDetail, updateLead, addActivity, addOpening, deleteOpening, updateFile, API, getToken } from '@/lib/api';
import { ArrowLeft, Zap, Phone, Mail, MapPin, Plus, Trash2, Eye, AlertTriangle, Send, FileText, Image, LayoutGrid, Activity, Download } from 'lucide-react';

const STATUSES = ['New Submission', 'Reviewing Photos', 'Needs More Info', 'Quote in Progress', 'Quote Sent', 'Follow-Up', 'Sold', 'Lost'];
const OPENING_TYPES = ['Single Hung', 'Horizontal Roller', 'Picture Window', 'Casement', 'Entry Door', 'French Door', 'Sliding Glass Door', 'Storefront / Commercial', 'Not Sure'];

const statusColor = (status) => {
  const colors = {
    'New Submission': 'bg-blue-100 text-blue-700 border-blue-200',
    'Reviewing Photos': 'bg-yellow-100 text-yellow-700 border-yellow-200',
    'Needs More Info': 'bg-orange-100 text-orange-700 border-orange-200',
    'Quote in Progress': 'bg-purple-100 text-purple-700 border-purple-200',
    'Quote Sent': 'bg-green-100 text-green-700 border-green-200',
    'Follow-Up': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    'Sold': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'Lost': 'bg-red-100 text-red-700 border-red-200'
  };
  return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
};

export default function AdminLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [showAddOpening, setShowAddOpening] = useState(false);
  const [newOpening, setNewOpening] = useState({ room_name: '', opening_type: 'Not Sure', width: '', height: '', quantity: 1, notes: '' });
  const [selectedImage, setSelectedImage] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const resp = await getLeadDetail(id);
      setData(resp.data);
    } catch (err) {
      toast.error('Failed to load lead');
      navigate('/admin');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusChange = async (newStatus) => {
    try {
      await updateLead(id, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      loadData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await addActivity(id, { activity_type: 'note', description: noteText });
      setNoteText('');
      toast.success('Note added');
      loadData();
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleAddOpening = async () => {
    try {
      await addOpening(id, {
        ...newOpening,
        width: newOpening.width ? parseFloat(newOpening.width) : null,
        height: newOpening.height ? parseFloat(newOpening.height) : null
      });
      setNewOpening({ room_name: '', opening_type: 'Not Sure', width: '', height: '', quantity: 1, notes: '' });
      setShowAddOpening(false);
      toast.success('Opening added');
      loadData();
    } catch (err) {
      toast.error('Failed to add opening');
    }
  };

  const handleDeleteOpening = async (openingId) => {
    if (!window.confirm('Delete this opening?')) return;
    try {
      await deleteOpening(openingId);
      toast.success('Opening deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete opening');
    }
  };

  const handleToggleUnclear = async (fileId, current) => {
    try {
      await updateFile(fileId, { unclear: !current });
      toast.success(current ? 'Unmarked as unclear' : 'Marked as unclear');
      loadData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (loading || !data) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <div className="animate-pulse text-[var(--brand-slate-600)]">Loading lead details...</div>
    </div>
  );

  const lead = data.lead;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[hsl(var(--border))]">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-[var(--brand-slate-600)] hover:text-[var(--brand-ocean-700)]">
              <ArrowLeft className="h-4 w-4 mr-1" /> Leads
            </Button>
            <Separator orientation="vertical" className="h-5" />
            <span className="font-heading font-semibold text-sm text-[var(--brand-slate-900)]">{lead.first_name} {lead.last_name}</span>
          </div>
          <Button onClick={() => navigate(`/admin/quotes/new/${id}`)} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-9 px-4 text-sm font-semibold">
            <FileText className="h-4 w-4 mr-1" /> Create Quote
          </Button>
        </div>
      </nav>

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-[var(--brand-slate-900)] mb-2">{lead.first_name} {lead.last_name}</h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--brand-slate-600)]">
              <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {lead.phone}</span>
              <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {lead.email}</span>
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {lead.city}, FL</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={lead.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[220px] border-2">
                <Badge className={`${statusColor(lead.status)} font-medium border`}>{lead.status}</Badge>
              </SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" data-testid="admin-lead-detail-tabs">
          <TabsList className="mb-4 h-11">
            <TabsTrigger value="overview" className="gap-1.5"><FileText className="h-4 w-4" /> Overview</TabsTrigger>
            <TabsTrigger value="photos" className="gap-1.5"><Image className="h-4 w-4" /> Photos ({data.files?.length || 0})</TabsTrigger>
            <TabsTrigger value="openings" className="gap-1.5"><LayoutGrid className="h-4 w-4" /> Openings ({data.openings?.length || 0})</TabsTrigger>
            <TabsTrigger value="quotes" className="gap-1.5"><FileText className="h-4 w-4" /> Quotes ({data.quotes?.length || 0})</TabsTrigger>
            <TabsTrigger value="activity" className="gap-1.5"><Activity className="h-4 w-4" /> Activity</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="rounded-2xl border-2">
                <CardContent className="p-5">
                  <h3 className="font-heading font-semibold mb-4 text-[var(--brand-slate-900)]">Contact Information</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong className="text-[var(--brand-slate-900)]">Name:</strong> <span className="text-[var(--brand-slate-600)]">{lead.first_name} {lead.last_name}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">Phone:</strong> <span className="text-[var(--brand-slate-600)]">{lead.phone}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">Email:</strong> <span className="text-[var(--brand-slate-600)]">{lead.email}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">Address:</strong> <span className="text-[var(--brand-slate-600)]">{lead.property_address}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">City:</strong> <span className="text-[var(--brand-slate-600)]">{lead.city} {lead.county && `(${lead.county})`}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">ZIP:</strong> <span className="text-[var(--brand-slate-600)]">{lead.zip_code}</span></p>
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-2">
                <CardContent className="p-5">
                  <h3 className="font-heading font-semibold mb-4 text-[var(--brand-slate-900)]">Project Details</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong className="text-[var(--brand-slate-900)]">Property Type:</strong> <span className="text-[var(--brand-slate-600)]">{lead.property_type}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">Project Type:</strong> <span className="text-[var(--brand-slate-600)]">{lead.project_type}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">Windows:</strong> <span className="text-[var(--brand-slate-600)]">{lead.num_windows}</span> | <strong className="text-[var(--brand-slate-900)]">Doors:</strong> <span className="text-[var(--brand-slate-600)]">{lead.num_doors}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">Full House:</strong> <span className="text-[var(--brand-slate-600)]">{lead.full_house ? 'Yes' : 'No'}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">Hurricane Protection:</strong> <span className="text-[var(--brand-slate-600)]">{lead.hurricane_protection}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">HOA:</strong> <span className="text-[var(--brand-slate-600)]">{lead.hoa}</span> | <strong className="text-[var(--brand-slate-900)]">Financing:</strong> <span className="text-[var(--brand-slate-600)]">{lead.financing_interest}</span></p>
                    <p><strong className="text-[var(--brand-slate-900)]">Timeline:</strong> <span className="text-[var(--brand-slate-600)]">{lead.desired_timeframe}</span></p>
                    {lead.budget_range && <p><strong className="text-[var(--brand-slate-900)]">Budget:</strong> <span className="text-[var(--brand-slate-600)]">{lead.budget_range}</span></p>}
                    {lead.notes && <p><strong className="text-[var(--brand-slate-900)]">Notes:</strong> <span className="text-[var(--brand-slate-600)]">{lead.notes}</span></p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Photos */}
          <TabsContent value="photos">
            {data.files?.length === 0 ? (
              <Card className="rounded-2xl border-2"><CardContent className="p-12 text-center text-[var(--brand-slate-600)]">
                <Image className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No photos uploaded</p>
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-lead-photos-carousel">
                {data.files?.map((file) => (
                  <Card key={file.id} className={`rounded-2xl overflow-hidden group border-2 ${file.unclear ? 'ring-2 ring-orange-400' : ''}`}>
                    <div className="relative cursor-pointer" onClick={() => setSelectedImage(file)}>
                      <img src={`${API}/files/${file.id}`} alt={file.label || file.original_name} className="w-full h-44 object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
                        <Eye className="h-7 w-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </div>
                      {file.unclear && (
                        <div className="absolute top-2 left-2">
                          <Badge className="bg-orange-100 text-orange-700 text-xs border border-orange-300"><AlertTriangle className="h-3 w-3 mr-1" /> Unclear</Badge>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-2">
                      <p className="text-xs text-[var(--brand-slate-600)] truncate mb-1">{file.label || file.original_name}</p>
                      <Button variant="ghost" size="sm" className="h-7 text-xs px-2 w-full" onClick={(e) => { e.stopPropagation(); handleToggleUnclear(file.id, file.unclear); }}>
                        <AlertTriangle className="h-3 w-3 mr-1" /> {file.unclear ? 'Clear' : 'Mark Unclear'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {/* Image Preview Dialog */}
            {selectedImage && (
              <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
                <DialogContent className="max-w-4xl">
                  <DialogHeader><DialogTitle>{selectedImage.label || selectedImage.original_name}</DialogTitle></DialogHeader>
                  <img src={`${API}/files/${selectedImage.id}`} alt="Full size" className="w-full rounded-xl" />
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          {/* Openings */}
          <TabsContent value="openings">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading font-semibold text-[var(--brand-slate-900)]">Openings</h3>
              <Button variant="outline" size="sm" onClick={() => setShowAddOpening(true)} className="rounded-xl border-2">
                <Plus className="h-4 w-4 mr-1" /> Add Opening
              </Button>
            </div>
            {data.openings?.length === 0 ? (
              <Card className="rounded-2xl border-2"><CardContent className="p-12 text-center text-[var(--brand-slate-600)]">
                <LayoutGrid className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No openings added yet</p>
              </CardContent></Card>
            ) : (
              <Card className="rounded-2xl overflow-hidden border-2">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[hsl(var(--secondary))]">
                      <TableHead className="font-heading font-semibold">Room</TableHead>
                      <TableHead className="font-heading font-semibold">Type</TableHead>
                      <TableHead className="font-heading font-semibold">Dimensions</TableHead>
                      <TableHead className="font-heading font-semibold">Qty</TableHead>
                      <TableHead className="font-heading font-semibold">Notes</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.openings?.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell className="font-medium text-[var(--brand-slate-900)]">{o.room_name || '-'}</TableCell>
                        <TableCell className="text-[var(--brand-slate-600)]">{o.opening_type}</TableCell>
                        <TableCell className="text-sm text-[var(--brand-slate-600)]">{o.width && o.height ? `${o.width}" x ${o.height}"` : '-'}</TableCell>
                        <TableCell className="text-[var(--brand-slate-900)]">{o.quantity}</TableCell>
                        <TableCell className="text-sm text-[var(--brand-slate-600)]">{o.notes || '-'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteOpening(o.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
            {/* Add Opening Dialog */}
            <Dialog open={showAddOpening} onOpenChange={setShowAddOpening}>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Opening</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Room Name</Label><Input value={newOpening.room_name} onChange={e => setNewOpening(p => ({...p, room_name: e.target.value}))} className="mt-1" /></div>
                    <div><Label className="text-xs">Type</Label><Select value={newOpening.opening_type} onValueChange={v => setNewOpening(p => ({...p, opening_type: v}))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{OPENING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs">Width (in)</Label><Input type="number" value={newOpening.width} onChange={e => setNewOpening(p => ({...p, width: e.target.value}))} className="mt-1" /></div>
                    <div><Label className="text-xs">Height (in)</Label><Input type="number" value={newOpening.height} onChange={e => setNewOpening(p => ({...p, height: e.target.value}))} className="mt-1" /></div>
                    <div><Label className="text-xs">Qty</Label><Input type="number" min="1" value={newOpening.quantity} onChange={e => setNewOpening(p => ({...p, quantity: parseInt(e.target.value) || 1}))} className="mt-1" /></div>
                  </div>
                  <div><Label className="text-xs">Notes</Label><Input value={newOpening.notes} onChange={e => setNewOpening(p => ({...p, notes: e.target.value}))} className="mt-1" /></div>
                  <Button onClick={handleAddOpening} className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-10 font-semibold">Add Opening</Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Quotes */}
          <TabsContent value="quotes">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading font-semibold text-[var(--brand-slate-900)]">Quotes</h3>
              <Button onClick={() => navigate(`/admin/quotes/new/${id}`)} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-9 px-4 text-sm font-semibold">
                <Plus className="h-4 w-4 mr-1" /> New Quote
              </Button>
            </div>
            {data.quotes?.length === 0 ? (
              <Card className="rounded-2xl border-2"><CardContent className="p-12 text-center text-[var(--brand-slate-600)]">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" /><p className="mb-4">No quotes yet</p>
                <Button onClick={() => navigate(`/admin/quotes/new/${id}`)} variant="outline" className="rounded-xl border-2">Create First Quote</Button>
              </CardContent></Card>
            ) : (
              <div className="space-y-4">
                {data.quotes.map((q) => (
                  <Card key={q.id} className="rounded-2xl border-2">
                    <CardContent className="p-5">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-mono text-sm font-medium text-[var(--brand-slate-900)]">{q.quote_number}</p>
                          <p className="text-xs text-[var(--brand-slate-600)] mt-0.5">{new Date(q.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="border-2">{q.status}</Badge>
                          <a href={`${API}/admin/quotes/${q.id}/pdf?token=${getToken()}`} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm" className="rounded-xl border-2" data-testid="admin-quote-export-pdf-button">
                              <Download className="h-3.5 w-3.5 mr-1" /> PDF
                            </Button>
                          </a>
                        </div>
                      </div>
                      {/* Quote options comparison */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="admin-quote-comparison">
                        {q.options?.map((opt, oi) => {
                          const isRecommended = oi === 1;
                          return (
                            <div key={oi} className={`border-2 rounded-xl p-4 ${isRecommended ? 'border-[var(--brand-seafoam-200)] bg-[var(--brand-seafoam-200)]/20 ring-2 ring-[var(--brand-seafoam-200)]' : 'border-[hsl(var(--border))]'}`}>
                              <div className="flex justify-between items-start mb-2">
                                <Badge className={isRecommended ? 'bg-[var(--brand-seafoam-200)] text-[var(--brand-ocean-700)]' : 'bg-[hsl(var(--secondary))] text-[var(--brand-slate-900)]'} variant="secondary">
                                  {opt.option_label || `Option ${oi + 1}`}
                                </Badge>
                                {isRecommended && <Badge variant="outline" className="text-xs border-green-600 text-green-700 bg-green-50">Best Value</Badge>}
                              </div>
                              <p className="font-heading font-bold text-xl text-[var(--brand-ocean-700)] mb-1">${(opt.estimated_price || 0).toLocaleString()}</p>
                              <p className="text-xs text-[var(--brand-slate-600)] font-medium">{opt.manufacturer_name}</p>
                              {opt.product_line_name && <p className="text-xs text-[var(--brand-slate-600)]">{opt.product_line_name}</p>}
                              {opt.lead_time_text && <p className="text-xs text-[var(--brand-slate-600)] mt-2">⏱ {opt.lead_time_text}</p>}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between items-center mt-4 pt-4 border-t">
                        <span className="text-sm text-[var(--brand-slate-600)]">Total Estimate: <strong className="font-heading text-[var(--brand-ocean-700)] text-base">${(q.total || 0).toLocaleString()}</strong></span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Activity */}
          <TabsContent value="activity">
            <Card className="rounded-2xl mb-4 border-2">
              <CardContent className="p-4">
                <div className="flex gap-2">
                  <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." rows={2} className="flex-1" />
                  <Button onClick={handleAddNote} disabled={!noteText.trim()} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl self-end h-10">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            <div className="space-y-3">
              {data.activities?.map((a, i) => (
                <Card key={i} className="rounded-xl border-2">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${a.activity_type === 'status_change' ? 'bg-blue-100 text-blue-600' : a.activity_type === 'quote_created' ? 'bg-green-100 text-green-600' : 'bg-[hsl(var(--secondary))] text-[var(--brand-slate-600)]'}`}>
                        {a.activity_type === 'status_change' ? <Activity className="h-4 w-4" /> : a.activity_type === 'quote_created' ? <FileText className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[var(--brand-slate-900)]">{a.description}</p>
                        <p className="text-xs text-[var(--brand-slate-600)] mt-1">{new Date(a.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {data.activities?.length === 0 && <Card className="rounded-2xl border-2"><CardContent className="p-8 text-center text-[var(--brand-slate-600)]">No activity yet</CardContent></Card>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
